/**
 * Shared field mapping for October wedding templates.
 * Avoids ghost demo defaults (Rohan & Isha, fake menus) — see
 * docs/bug fixes/marathi lagn bug.md + docs/ai-org/REVIEW-CHECKLIST.md §2.3.
 */
import { parseFlexibleDate } from '@/src/infrastructure/lib/weddingDataHelper';
import { safeImageUrl } from '@/src/modules/renderer/url-safety';
import type { InvitationData } from '../types';

/**
 * Extra fields for code-rendered experience templates. The wizard's Details step (ExperienceDetailsStep)
 * saves names, date, tagline and photos under `customData.experience.meta`; AI/legacy data may use
 * `customData.expMeta`. Templates must read both (experience.meta wins) — reading only `expMeta`
 * silently dropped every wizard-entered photo and name (#481, #482).
 */
export function resolveExpMeta(data: InvitationData): Record<string, unknown> {
  const cd = data.customData as
    | { expMeta?: Record<string, unknown>; experience?: { meta?: Record<string, unknown> } }
    | null
    | undefined;
  const merged: Record<string, unknown> = { ...(cd?.expMeta ?? {}) };
  for (const [k, v] of Object.entries(cd?.experience?.meta ?? {})) {
    if (v == null || v === '') continue;
    if (k === 'referenceImages' && typeof v === 'object') {
      merged.referenceImages = { ...((merged.referenceImages as object) ?? {}), ...(v as object) };
    } else merged[k] = v;
  }
  return merged;
}

export function pickWeddingStr(...vals: unknown[]): string {
  for (const v of vals) {
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

export type OctoberCeremonySlot = {
  key: string;
  label: string;
  date?: string;
  time?: string;
  venue?: string;
  coverImageUrl?: string;
  description?: string;
  filled: boolean;
};

export type OctoberGalleryItem = {
  key: string;
  url: string;
  caption?: string;
  isVideo: boolean;
};

export type OctoberMenuItem = {
  title: string;
  note: string;
  filled: boolean;
};

type RawEvent = {
  title?: string;
  name?: string;
  nameEn?: string;
  date?: string;
  startTime?: string;
  time?: string;
  venue?: string;
  coverImageUrl?: string;
  description?: string;
  note?: string;
  visible?: boolean;
};

function slotAliases(slot: string): string[] {
  const s = slot.toLowerCase();
  if (s === 'vivah' || s === 'wedding') return ['vivah', 'wedding', 'lagna', 'shaadi', 'ceremony'];
  if (s === 'after-party' || s === 'after party') return ['after-party', 'after party', 'afterparty'];
  return [s];
}

function eventTitle(ev: RawEvent): string {
  return pickWeddingStr(ev.title, ev.nameEn, ev.name);
}

function eventHasContent(ev: RawEvent): boolean {
  return Boolean(pickWeddingStr(ev.date, ev.startTime, ev.time, ev.venue, ev.description, ev.note, ev.coverImageUrl));
}

function collectRawEvents(data: InvitationData): RawEvent[] {
  const wedding = data.customData?.wedding;
  const fromEvents = Array.isArray(wedding?.weddingEvents) ? wedding!.weddingEvents! : [];
  const fromCeremonies = Array.isArray(wedding?.ceremonies) ? (wedding!.ceremonies as RawEvent[]) : [];
  const merged = [...fromEvents, ...fromCeremonies].filter((ev) => ev && ev.visible !== false);
  return merged;
}

/** Map wizard Wedding events (+ ceremonies alias) onto display slots. Title alone ≠ filled. */
export function resolveCeremonySlots(
  data: InvitationData,
  slots: readonly string[],
  fallbackVenue = '',
): OctoberCeremonySlot[] {
  const events = collectRawEvents(data);
  const used = new Set<number>();

  const matchIndex = (slot: string, index: number) => {
    const aliases = slotAliases(slot);
    const byTitle = events.findIndex((ev, i) => {
      if (used.has(i)) return false;
      const t = eventTitle(ev).toLowerCase();
      return aliases.some((a) => t.includes(a));
    });
    if (byTitle >= 0) return byTitle;
    if (events[index] && !used.has(index) && eventHasContent(events[index])) return index;
    return -1;
  };

  const toSlot = (ev: RawEvent, slot: string, i: number): OctoberCeremonySlot => {
    const parsed = parseFlexibleDate(ev.date);
    const dateLabel = parsed
      ? parsed.toLocaleDateString('en-IN', {
          weekday: 'short',
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })
      : pickWeddingStr(ev.date);
    return {
      key: `c-${i}-${slot}`,
      label: pickWeddingStr(eventTitle(ev), slot),
      date: dateLabel || undefined,
      time: pickWeddingStr(ev.startTime, ev.time) || undefined,
      venue: pickWeddingStr(ev.venue, fallbackVenue) || undefined,
      coverImageUrl: safeImageUrl(pickWeddingStr(ev.coverImageUrl)) || undefined,
      description: pickWeddingStr(ev.description, ev.note) || undefined,
      filled: eventHasContent(ev),
    };
  };

  const mapped = slots.map((slot, i) => {
    const idx = matchIndex(slot, i);
    if (idx < 0) {
      return { key: `slot-${slot}`, label: slot, filled: false } as OctoberCeremonySlot;
    }
    used.add(idx);
    return toSlot(events[idx], slot, i);
  });

  // The wizard offers more event rows than a template has named slots. Never drop a host's
  // filled event just because it matched no slot — append it after the named ones.
  const extras = events
    .map((ev, i) => ({ ev, i }))
    .filter(({ ev, i }) => !used.has(i) && eventHasContent(ev))
    .map(({ ev, i }, n) => toSlot(ev, `Event ${slots.length + n + 1}`, slots.length + n + i));

  return [...mapped, ...extras];
}

/** Couple photos: groom/bride uploads + mediaUrls + mediaShowcase (Details gallery). */
export function resolveCouplePhotos(data: InvitationData) {
  const wedding = data.customData?.wedding;
  const expMeta = resolveExpMeta(data);
  const ref = expMeta.referenceImages as
    | { couple?: string; primary?: string; bride?: string; groom?: string }
    | undefined;

  const bridePhoto = safeImageUrl(
    pickWeddingStr(
      wedding?.bride?.photoUrl,
      ref?.bride,
      expMeta.bridePhotoUrl as string,
      (data.customData as { bridePhotoUrl?: string } | null)?.bridePhotoUrl,
    ),
  );
  const groomPhoto = safeImageUrl(
    pickWeddingStr(
      wedding?.groom?.photoUrl,
      ref?.groom,
      expMeta.groomPhotoUrl as string,
      (data.customData as { groomPhotoUrl?: string } | null)?.groomPhotoUrl,
    ),
  );
  const couplePhoto = safeImageUrl(
    pickWeddingStr(
      ref?.couple,
      ref?.primary,
      expMeta.couplePhotoUrl as string,
      data.mediaUrls?.[0],
      wedding?.groom?.photoUrl,
      wedding?.bride?.photoUrl,
    ),
  );

  return { bridePhoto, groomPhoto, couplePhoto, heroPortrait: couplePhoto || groomPhoto || bridePhoto };
}

/**
 * Gallery for couples: mediaShowcase (when enabled / has URLs) + remaining mediaUrls.
 * Never invent stock photos.
 */
export function resolveCoupleGallery(data: InvitationData): OctoberGalleryItem[] {
  const wedding = data.customData?.wedding;
  const showcaseOn = wedding?.showMediaShowcase !== false;
  const items: OctoberGalleryItem[] = [];
  const seen = new Set<string>();

  const push = (url: string, caption?: string, isVideo = false) => {
    const safe = safeImageUrl(url) || (isVideo ? url : '');
    if (!safe || seen.has(safe)) return;
    seen.add(safe);
    items.push({ key: `g-${items.length}`, url: safe, caption, isVideo });
  };

  if (showcaseOn && Array.isArray(wedding?.mediaShowcase)) {
    for (const m of wedding.mediaShowcase) {
      if (!m?.url?.trim()) continue;
      push(m.url, pickWeddingStr(m.caption, m.description) || undefined, m.type === 'video');
    }
  }

  for (const url of data.mediaUrls ?? []) {
    push(url);
  }

  return items;
}

/**
 * Menu / evening notes — only user-authored content.
 * Sources: expMeta.menuItems, or Reception/Wedding event description lines.
 * Empty → placeholder slots (no ghost "Champagne tower").
 */
export function resolveMenuItems(data: InvitationData, placeholderCount = 4): OctoberMenuItem[] {
  const expMeta = resolveExpMeta(data);
  const raw = expMeta.menuItems;

  if (Array.isArray(raw) && raw.length > 0) {
    const fromMeta = raw
      .map((item, i) => {
        if (typeof item === 'string') {
          const title = item.trim();
          return title ? { title, note: '', filled: true } : null;
        }
        if (item && typeof item === 'object') {
          const o = item as { title?: string; note?: string; description?: string };
          const title = pickWeddingStr(o.title);
          const note = pickWeddingStr(o.note, o.description);
          if (!title && !note) return null;
          return { title: title || `Course ${i + 1}`, note, filled: true };
        }
        return null;
      })
      .filter((x): x is OctoberMenuItem => Boolean(x));
    if (fromMeta.length > 0) return fromMeta;
    // All rows blank (editor placeholders) → show actionable empty slots below
  }

  const events = collectRawEvents(data);
  const receptionish = events.find((ev) => {
    const t = eventTitle(ev).toLowerCase();
    return t.includes('reception') || t.includes('cocktail') || t.includes('dinner');
  });
  const blob = pickWeddingStr(receptionish?.description, receptionish?.note);
  if (blob) {
    const lines = blob
      .split(/[\n;|]+/)
      .map((l) => l.trim())
      .filter(Boolean);
    if (lines.length > 0) {
      return lines.map((line) => {
        const [title, ...rest] = line.split(/[—–:\-]/).map((s) => s.trim());
        return {
          title: title || line,
          note: rest.join(' — '),
          filled: true,
        };
      });
    }
  }

  return Array.from({ length: placeholderCount }, (_, i) => ({
    title: `Course ${i + 1}`,
    note: 'Add in Details → Evening menu',
    filled: false,
  }));
}

/** Evening flow from real event start times when present; else soft placeholders. */
export function resolveEveningBeats(
  data: InvitationData,
): { t: string; label: string; filled: boolean }[] {
  const events = collectRawEvents(data).filter((ev) => pickWeddingStr(ev.startTime, ev.time));
  if (events.length > 0) {
    return events.slice(0, 6).map((ev) => ({
      t: pickWeddingStr(ev.startTime, ev.time),
      label: eventTitle(ev) || 'Celebration',
      filled: true,
    }));
  }
  const door = pickWeddingStr(data.eventTime);
  return [
    { t: door || 'TBD', label: 'Doors open', filled: Boolean(door) },
    { t: 'TBD', label: 'Grand entrance', filled: false },
    { t: 'TBD', label: 'Dinner & toasts', filled: false },
    { t: 'TBD', label: 'Dance floor', filled: false },
  ];
}
